// Which shortcuts the home Manage menu and the top-bar Screens menu show
// (2026-10-22, homeLayout.js), plus the Manage menu's Customize mode: an eye
// on each shortcut, Reset and Done. Hiding never turns a feature off; the
// shortcut just leaves the menu. Hidden items stay listed in Settings > Home
// screen. Catalogue and Help have no data-home-key, so they can't be hidden.

const EYE_ON  = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></svg>';
const EYE_OFF = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m3 3 18 18"/><path d="M10.6 5.1A10 10 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3 3.9"/><path d="M6.6 6.6A17 17 0 0 0 2 12s3.5 7 10 7a9.7 9.7 0 0 0 4.4-1"/></svg>';

class HomeNavView {
  _nav  = document.getElementById('homeSideNav');
  _list = document.getElementById('homeSideNavList');
  _box  = document.getElementById('homeNavCustomize');
  _editing = false;
  _draft   = null;   // Set of hidden keys while customizing
  _hidden  = new Set();
  _tablesOn = false;

  constructor() {
    // One eye per hideable shortcut; CSS shows them only while customizing.
    this._list?.querySelectorAll('[data-home-key]').forEach((el) => {
      const eye = document.createElement('span');
      eye.className = 'home-nav-eye';
      eye.setAttribute('aria-hidden', 'true');
      el.appendChild(eye);
    });
  }

  // hidden: keys left off the menus. tablesOn: table service isn't 'off'
  // (the Tables shortcut needs it no matter what the list says).
  apply({ hidden, tablesOn }) {
    this._hidden = new Set(hidden);
    this._tablesOn = tablesOn;
    if (this._editing) return this._paintEditing();
    document.querySelectorAll('[data-home-key]').forEach((el) => {
      const key = el.dataset.homeKey;
      const off = this._hidden.has(key) || (key === 'tables' && !tablesOn);
      el.classList.toggle('home-hidden', off);
    });
    this._syncScreensMenu();
  }

  // The Screens menu says so when every screen is hidden (the menu itself
  // stays, since tutorials point at it).
  _syncScreensMenu() {
    const menu = document.getElementById('displayDropdownMenu');
    if (!menu) return;
    let note = menu.querySelector('.nav-display-empty');
    const allHidden = [...menu.querySelectorAll('[data-home-key]')].every((el) => el.classList.contains('home-hidden'));
    if (allHidden && !note) {
      note = document.createElement('p');
      note.className = 'nav-display-empty';
      note.textContent = 'All screens are hidden. Show them in Settings > Home screen.';
      menu.appendChild(note);
    }
    note?.classList.toggle('hidden', !allHidden);
  }

  _paintEditing() {
    this._list?.querySelectorAll('[data-home-key]').forEach((el) => {
      const key = el.dataset.homeKey;
      if (key === 'tables' && !this._tablesOn) { el.classList.add('home-hidden'); return; }
      const off = this._draft.has(key);
      el.classList.remove('home-hidden');
      el.classList.toggle('home-nav-item--off', off);
      el.setAttribute('aria-pressed', String(!off));
      const label = el.querySelector('.home-side-nav-label')?.textContent.trim() ?? '';
      if (el.dataset.origLabel == null) el.dataset.origLabel = el.getAttribute('aria-label') ?? '';
      el.setAttribute('aria-label', `${off ? 'Show' : 'Hide'} ${label}`);
      const eye = el.querySelector('.home-nav-eye');
      if (eye) eye.innerHTML = off ? EYE_OFF : EYE_ON;
    });
  }

  _setEditing(on) {
    this._editing = on;
    this._nav?.classList.toggle('home-side-nav--editing', on);
    if (on) this._nav?.classList.add('home-side-nav--open');
    this._box?.classList.toggle('home-nav-customize--editing', on);
    if (on) {
      this._draft = new Set(this._hidden);
      this._paintEditing();
    } else {
      this._list?.querySelectorAll('[data-home-key]').forEach((el) => {
        el.classList.remove('home-nav-item--off');
        el.removeAttribute('aria-pressed');
        if (el.dataset.origLabel != null) {
          el.setAttribute('aria-label', el.dataset.origLabel);
          delete el.dataset.origLabel;
        }
      });
      this._draft = null;
      this.apply({ hidden: [...this._hidden], tablesOn: this._tablesOn });
    }
  }

  get editing() { return this._editing; }

  // onSave(hiddenKeys) fires on Done when something changed.
  _addHandlerCustomize(onSave) {
    document.getElementById('homeNavCustomizeBtn')?.addEventListener('click', () => this._setEditing(true));
    document.getElementById('homeNavResetBtn')?.addEventListener('click', () => {
      this._draft = new Set();
      this._paintEditing();
    });
    document.getElementById('homeNavDoneBtn')?.addEventListener('click', () => {
      const next = [...this._draft];
      const changed = next.length !== this._hidden.size || next.some((k) => !this._hidden.has(k));
      if (changed) this._hidden = new Set(next);
      this._setEditing(false);
      if (changed) onSave(next);
    });
    // While customizing, a tap on a shortcut shows / hides it instead of
    // opening it. Capture phase, so the panels' own listeners never see it.
    this._list?.addEventListener('click', (e) => {
      if (!this._editing) return;
      const item = e.target.closest('.home-side-nav-item');
      if (!item) return;
      e.preventDefault();
      e.stopPropagation();
      const key = item.dataset.homeKey;
      if (!key) return;
      if (this._draft.has(key)) this._draft.delete(key);
      else this._draft.add(key);
      this._paintEditing();
    }, true);
  }
}

export default new HomeNavView();
